import mitt from 'mitt';
import { trackPromise } from 'react-promise-tracker';
import { Index, StatusCode, TimeInMs } from 'utils/consts';

import {
  RequestMethod,
  SendRequestParams,
  ResponseData,
  MessagesType,
  messages
} from './Api.model';
import { Token } from '../Token';
import {
  badRequestCode,
  failedToFetch,
  generalError,
  generalErrorSuccessFalse,
  notFoundCode
} from './handleErrorResponse';

const { REACT_APP_API_URL } = process.env;

export const events = mitt();
export const apiEvents = {
  WSS_CLOSE_CONNECTION: 'WSS_CLOSE_CONNECTION',
  WSS_DOCUMENT_GENERATION: 'WSS_DOCUMENT_GENERATION',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  GENERAL_WARN: 'GENERAL_WARN',
  GENERAL_ERROR: 'GENERAL_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  ERROR: 'ERROR',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SIGMA_TIMEOUT: 'SIGMA_TIMEOUT'
};

export const appEvents = {
  CLEAR_TIMEOUT: 'CLEAR_TIMEOUT'
};

const getHeaders = (token?: string): Record<string, string> => ({
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json',
  ...(token && { Authorization: token })
});

const getBody = <T>(payload?: T) => payload && JSON.stringify(payload);

const sendRequest = <T, D>(
  params: SendRequestParams<T>,
  noTrack: boolean,
  timeoutOn?: boolean
): Promise<D> => {
  const token = Token.getBearer();
  const headers = getHeaders(token);
  const body = getBody(params.payload);
  const { method } = params;
  const abortController = new AbortController();
  const timeout = setTimeout(
    () => {
      abortController.abort();
      events.emit(apiEvents.SIGMA_TIMEOUT);
    },
    timeoutOn ? TimeInMs.thirtySeconds : TimeInMs.fiveMinutes
  );
  const request = fetch(`${REACT_APP_API_URL}${params.endpoint}`, {
    headers,
    body,
    method,
    signal: abortController.signal
  })
    .then((response: Response) => {
      const HttpStatusCodePattern = /([4][0-9][0-9])|([5][0-9]{2})/;
      const HttpOkOrCreatedStatusCodePattern = /20[01]/;
      clearInterval(timeout);
      if (response.status === StatusCode.badRequestCode) {
        badRequestCode<D>(response);
      } else if (
        response.status === StatusCode.unauthorizedCode &&
        params.endpoint !== '/auth/login'
      ) {
        events.emit(apiEvents.TOKEN_EXPIRED);
      } else if (
        (response.status === StatusCode.unauthorizedCode ||
          response.status === StatusCode.forbiddenCode) &&
        params.endpoint === '/auth/login'
      ) {
        //  This case is handled in user.effects loginUser
      } else if (response.status === StatusCode.notFoundCode) {
        notFoundCode<D>(response);
      } else if (HttpStatusCodePattern.test(response.status.toString())) {
        generalError<D>(response);
      }

      const contentType = response.headers.get('content-type');

      if (
        contentType &&
        contentType.indexOf('application/json') !== Index.minusOne
      ) {
        return (response.json() as Promise<ResponseData<D>>).then((data) => {
          if (
            HttpOkOrCreatedStatusCodePattern.test(response.status.toString()) &&
            data?.success === false &&
            !messages.includes(data?.message as unknown as MessagesType)
          ) {
            generalErrorSuccessFalse(data, response.status);
          }
          return {
            ...data,
            status: response.status
          };
        });
      }
      return response as any;
    })
    .catch((error) => {
      if (error.message === 'Failed to fetch') {
        failedToFetch(error);
      }
      throw error;
    });
  return noTrack ? request : trackPromise(request);
};

const sendGet = <T, D>(endpoint: string, noTrack = false, timeoutOn = false) =>
  sendRequest<T, D>(
    { endpoint, method: RequestMethod.GET },
    noTrack,
    timeoutOn
  );

const sendPost = <T, D>(endpoint: string, payload?: T, noTrack = false) =>
  sendRequest<T, D>({ endpoint, payload, method: RequestMethod.POST }, noTrack);

const sendPut = <T, D>(endpoint: string, payload?: T, noTrack = false) =>
  sendRequest<T, D>({ endpoint, payload, method: RequestMethod.PUT }, noTrack);

const sendPatch = <T, D>(
  endpoint: string,
  payload?: Partial<T>,
  noTrack = false
) =>
  sendRequest<T, D>(
    { endpoint, payload, method: RequestMethod.PATCH },
    noTrack
  );

const sendDelete = <T, D>(endpoint: string, payload?: T, noTrack = false) =>
  sendRequest<T, D>(
    { endpoint, payload, method: RequestMethod.DELETE },
    noTrack
  );

export default {
  get: sendGet,
  post: sendPost,
  put: sendPut,
  patch: sendPatch,
  delete: sendDelete
};
